import { isPlatformBrowser } from '@angular/common';
import { Injectable, Inject, PLATFORM_ID, NgZone, OnDestroy } from '@angular/core';
import { ICacheService } from './contracts/i-cache.service';
import { CacheConfiguration } from './models/cache.configuration';
import {
  CacheStoragesEnum,
  ICacheInfo,
  ICacheLogger,
  I_CACHE_LOGGER,
  INgCacheOptions,
  INgCacheStoreOptions,
  IStorageValue,
  ITagsMap,
} from './models/cache.models';
import { CacheStorageBase, CacheStorageHybrid, CacheStorageLocal, CacheStorageMemory, CacheStorageSession } from './storage/index';

const CACHE_PREFIX = 'Nimbus.UI.CacheService';
const DEFAULT_ENABLED_STORAGE = CacheStoragesEnum.MEMORY;

@Injectable({ providedIn: 'root' })
export class NgCacheService implements ICacheService, OnDestroy {
  private timeoutHandle: number;

  private defaultOptions: INgCacheStoreOptions = {
    cacheExpires: 0,
    preloadExpires: Number.MAX_VALUE,
  };

  private prefix: string = CACHE_PREFIX;

  private mainStorage: CacheStorageBase;
  private readonly fallbackStorageTypes: CacheStoragesEnum[];

  public constructor(
    storageTypes: CacheConfiguration,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(I_CACHE_LOGGER) private readonly logger: ICacheLogger,
    private readonly ngZone: NgZone,
  ) {
    if (!storageTypes || storageTypes.length < 1) {
      throw new Error('Please specify storage types');
    }

    const mainStorageType = storageTypes[0];
    this.fallbackStorageTypes = storageTypes.slice(1);

    this.mainStorage = this.initStorage(mainStorageType);
    this.validateStorage(this.fallbackStorageTypes);

    const storageType = this.getStorageType();
    if (!storageType) {
      this.logger.error(
        'Cache storage type is not defined | It can lead to unexpected behavior or some missed data if/when user refresh page',
      );
    } else {
      if (isPlatformBrowser(this.platformId)) {
        if (
          storageType === CacheStoragesEnum.MEMORY &&
          storageTypes.filter((fst) => fst === CacheStoragesEnum.LOCAL_STORAGE || fst === CacheStoragesEnum.SESSION_STORAGE).length > 0
        ) {
          this.logger.error(
            'It is using MEMORY cache storage type in browser | The expected storage type is LocalStorage or MemoryStorage | It can lead to missed data if/when user refresh page',
          );
        }
      } else {
        if (storageType !== CacheStoragesEnum.MEMORY) {
          this.logger.error(
            'It is NOT using MEMORY cache storage type in server | The expected storage type is MemoryStorage | It can lead to unexpected behavior in server environment',
          );
        }
      }
    }

    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        this.timeoutHandle = setTimeout(() => this.handleExpiration(), 1000 * 60 /* 1 minute */) as any as number;
      });
    }
  }

  public ngOnDestroy(): void {
    clearTimeout(this.timeoutHandle);
    this.mainStorage.ngOnDestroy();
  }

  public set(key: string, value: any, options?: INgCacheOptions): boolean {
    if (!this.isEnabled()) {
      return false;
    }

    const storageKey = this.toStorageKey(key);
    options = options ? options : this.defaultOptions;

    const itemLength = this.mainStorage.setItem(storageKey, this.toStorageValue(value, options));
    if (itemLength === false) {
      return false;
    }

    if (!NgCacheService.isSystemKey(key) && options.tag) {
      this.saveTag(options.tag, storageKey);
    }
    if (itemLength > 1000 && (!options || (!options.cacheExpires && !options.cacheMaxAge))) {
      this.saveBigObjectKey(key);
    }

    return true;
  }

  public getCacheValueInfo<TEntity>(key: string, retrieve?: (entity: TEntity) => TEntity): ICacheInfo<TEntity> {
    if (!this.isEnabled()) {
      return undefined;
    }

    const storageValue = this.mainStorage.getItem<IStorageValue<TEntity>>(this.toStorageKey(key));
    if (!storageValue) {
      return undefined;
    }

    const validForCache = NgCacheService.validateForCache(storageValue);
    const validForPreload = NgCacheService.validateForPreload(storageValue);

    if (!validForCache && !validForPreload) {
      this.remove(key);
      return undefined;
    }

    return {
      value: retrieve && storageValue.value ? retrieve(storageValue.value) : storageValue.value,
      validForCache,
      validForPreload,
    };
  }

  public getCache<TEntity>(key: string, retrieve?: (entity: TEntity) => TEntity): TEntity | undefined {
    const cacheValueInfo = this.getCacheValueInfo(key, retrieve);
    if (cacheValueInfo === undefined || !cacheValueInfo.validForCache) {
      return undefined;
    }

    return cacheValueInfo.value;
  }

  public getPreload<TEntity>(key: string, retrieve?: (entity: TEntity) => TEntity): TEntity | undefined {
    const cacheValueInfo = this.getCacheValueInfo(key, retrieve);
    if (cacheValueInfo === undefined || !cacheValueInfo.validForPreload) {
      return undefined;
    }

    return cacheValueInfo.value;
  }

  public exists(key: string): boolean {
    return this.getCacheValueInfo(key) !== undefined;
  }

  public remove(key: string, check?: boolean) {
    this.mainStorage.removeItem(this.toStorageKey(key));
    if (this.mainStorage.isEnabled(check)) {
      this.removeFromTag(this.toStorageKey(key));
    }
  }

  public removeAll() {
    if (!this.isEnabled()) {
      return;
    }

    this.mainStorage.clear(this.prefix);
  }

  public getTagData<TEntity>(tag: string): { [key: string]: ICacheInfo<TEntity> } {
    if (!this.isEnabled()) {
      return undefined;
    }

    const tags = this.getItem<ITagsMap>(NgCacheService.tagsStorageKey());
    const tagKeys: string[] = tags && tags[tag];
    if (!tagKeys || tagKeys.length === 0) {
      return null;
    }

    return tagKeys.reduce(
      (agg, key) => {
        key = this.fromStorageKey(key);
        const data = this.getCacheValueInfo<TEntity>(key);
        if (data !== undefined) {
          agg[key] = data;
        }

        return agg;
      },
      {} as { [key: string]: ICacheInfo<TEntity> },
    );
  }

  public getItemExpirations(): { [key: string]: number } {
    if (!this.isEnabled()) {
      return {};
    }

    const tags = this.getItem<ITagsMap>(NgCacheService.tagsStorageKey());
    if (!tags) {
      return {};
    }

    return Object.keys(tags).reduce(
      (tagsAgg, tag) => {
        const tagValues = tags[tag];

        for (const valueKey of tagValues) {
          const value = this.getItem<IStorageValue<any>>(valueKey);

          tagsAgg[valueKey] = (value && value.options && (value.options.preloadExpires || value.options.cacheExpires)) || 0;
        }

        return tagsAgg;
      },
      {} as { [key: string]: number },
    );
  }

  public removeTag(tag: string) {
    if (!this.isEnabled()) {
      return;
    }

    const tags = this.getItem<ITagsMap>(NgCacheService.tagsStorageKey()) || {};
    if (tags[tag]) {
      tags[tag].forEach((key: string) => {
        this.mainStorage.removeItem(key);
      });
      delete tags[tag];
      this.set(NgCacheService.tagsStorageKey(), tags);
    }
  }

  public setGlobalPrefix(prefix: string) {
    this.prefix = prefix;
  }

  public getStorageType(): CacheStoragesEnum {
    if (this.mainStorage) {
      return this.mainStorage.type();
    }
    return undefined;
  }

  public persist(): void {
    this.mainStorage.persist();
  }

  public unPersist(): void {
    this.mainStorage.unPersist(this.prefix);
  }

  private validateStorage(fallbackStorageTypes: CacheStoragesEnum[]) {
    if (!this.mainStorage || !this.mainStorage.isEnabled()) {
      if (fallbackStorageTypes || fallbackStorageTypes.length > 0) {
        for (const fallbackStorageType of fallbackStorageTypes) {
          const fallbackStorage = this.initStorage(fallbackStorageType);

          if (fallbackStorage && fallbackStorage.isEnabled()) {
            this.mainStorage = fallbackStorage;
            break;
          }
        }
      }
    }

    if (!this.mainStorage || !this.mainStorage.isEnabled()) {
      this.mainStorage = this.initStorage(DEFAULT_ENABLED_STORAGE);
    }
  }

  private removeFromTag(key: string) {
    const tags = this.getItem<ITagsMap>(NgCacheService.tagsStorageKey()) || {};
    let index: number;
    for (const tag of Object.keys(tags)) {
      index = tags[tag].indexOf(key);
      if (index !== -1) {
        tags[tag].splice(index, 1);
        this.set(NgCacheService.tagsStorageKey(), tags);
        break;
      }
    }
  }

  private initStorage(type: CacheStoragesEnum) {
    let mainStorage: CacheStorageBase;
    switch (type) {
      case CacheStoragesEnum.SESSION_STORAGE: {
        mainStorage = new CacheStorageSession(this.logger);
        break;
      }
      case CacheStoragesEnum.LOCAL_STORAGE: {
        mainStorage = new CacheStorageLocal(this.logger);
        break;
      }
      case CacheStoragesEnum.HYBRID: {
        mainStorage = new CacheStorageHybrid(CACHE_PREFIX, this.ngZone, new CacheStorageLocal(this.logger), 1000);
        break;
      }
      default: {
        mainStorage = new CacheStorageMemory();
      }
    }
    return mainStorage;
  }

  private toStorageKey(key: string) {
    return this.getCachePrefix() + key;
  }

  private fromStorageKey(key: string) {
    return key.replace(this.getCachePrefix(), '');
  }

  private toStorageValue<TEntity>(value: TEntity, options: INgCacheOptions): IStorageValue<TEntity> {
    return {
      value,
      options: this.toStorageOptions(options),
    };
  }

  private toStorageOptions(options: INgCacheOptions): INgCacheOptions {
    const storageOptions: INgCacheOptions = {};
    storageOptions.cacheExpires =
      options.cacheExpires || (options.cacheMaxAge && Date.now() + options.cacheMaxAge) || this.defaultOptions.cacheExpires;
    storageOptions.preloadExpires =
      options.preloadExpires || (options.preloadMaxAge && Date.now() + options.preloadMaxAge) || this.defaultOptions.preloadExpires;

    return storageOptions;
  }

  private static validateForCache<TEntity>(value: IStorageValue<TEntity>) {
    return !!value.options.cacheExpires && value.options.cacheExpires > Date.now();
  }

  private static validateForPreload<TEntity>(value: IStorageValue<TEntity>) {
    return !!value.options.preloadExpires && value.options.preloadExpires > Date.now();
  }

  private static isSystemKey(key: string) {
    return [NgCacheService.tagsStorageKey()].indexOf(key) !== -1;
  }

  private saveTag(tag: string, key: string) {
    const tags = this.getItem<ITagsMap>(NgCacheService.tagsStorageKey()) || {};
    if (!tags[tag]) {
      tags[tag] = [key];
    } else {
      tags[tag].push(key);
    }
    this.setItem(NgCacheService.tagsStorageKey(), tags);
  }

  private saveBigObjectKey(key: string) {
    const bigObjects = this.getItem<string[]>(NgCacheService.bigObjectsKey()) || [];
    if (bigObjects.includes(key)) {
      return;
    }

    bigObjects.push(key);

    this.setItem(NgCacheService.bigObjectsKey(), bigObjects);
  }

  private getCachePrefix() {
    return this.prefix;
  }

  private static tagsStorageKey() {
    return 'CacheService_tags';
  }

  private static bigObjectsKey() {
    return 'CacheService_bigObjects';
  }

  private getItem<TItem>(key: string) {
    if (!this.isEnabled()) {
      return undefined;
    }

    return this.mainStorage.getItem<TItem>(key);
  }

  private setItem<TItem>(key: string, item: TItem): number | false {
    if (!this.isEnabled()) {
      return undefined;
    }

    return this.mainStorage.setItem<TItem>(key, item);
  }

  private isEnabled(check?: boolean): boolean {
    let isEnabled = this.mainStorage.isEnabled(check);
    if (!isEnabled) {
      const bigObjects = this.mainStorage.getItem<string[]>(NgCacheService.bigObjectsKey(), true);
      if (bigObjects && bigObjects.length > 0) {
        for (const key of bigObjects) {
          this.remove(key, true);
        }
      }

      try {
        this.mainStorage.setItem(NgCacheService.bigObjectsKey(), []);
      } catch {}

      isEnabled = this.mainStorage.isEnabled(true);
      if (!isEnabled) {
        this.mainStorage.clear(this.prefix);
      }
      isEnabled = this.mainStorage.isEnabled(true);
    }

    return isEnabled;
  }

  private handleExpiration() {
    const expirations = this.getItemExpirations();
    const expired = NgCacheService.getExpired(expirations);

    if (expired) {
      for (const exp of expired) {
        this.remove(exp);
      }
    }

    this.ngZone.runOutsideAngular(() => {
      this.timeoutHandle = setTimeout(() => this.handleExpiration(), 1000 * 60 * 10 /* 10 minutes */) as any as number;
    });
  }

  private static getExpired(items: { [key: string]: number }): string[] {
    let expired: string[] = null;

    const date = new Date().getTime();
    for (const itemKey of Object.keys(items)) {
      const itemExpiration = items[itemKey];

      if (itemExpiration < date) {
        (expired || (expired = [])).push(itemKey);
      }
    }

    return expired;
  }
}
